import React, { useState, useEffect } from 'react';

import { Modal, Button, Spin, Input, Dropdown, Badge } from 'antd';

import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import { useDispatch, useSelector } from 'react-redux';

import PdfViewer from './pdf-viewer';
import EditSettingsModal from './edit-field-modal';

import AssignSignersOrderModal from './assign-signers-order-modal';
import RabbitSignTemplateModal from './../rabbit-sign-templates/rabbit-sign-template-modal';

import * as rabbitActions from '../../../../../../../shared/store/actions';
import api from '../../../../../s3-connector/api';

import { isArray, isEmpty, isString, uniq } from 'lodash';

import { stringToArrayBuffer, isGuid, getRabbitSignClientOptionNameWithClientPreffix, mapRabbitSignField } from '../../../../../../../shared/utils';

import './field-settings-modal.less';

const FieldSettingsModal = ({
  rootContainer,
  open,
  onCancel,
  setRabbitSignFieldSettings,
  rabbitSignFieldSettings,
  selectedFiles,
  emails,
  setSignersOrder,
  combineAllPdfsIntoOne,
  enabledTemplates,
}) => {
  const [fileDataList, setFileDataList] = useState([]);
  const [currentFile, setCurrentFile] = useState(undefined);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMoreFiles, setHasMoreFiles] = useState(false);
  const [hasPrevFiles, setHasPrevFiles] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageHeights, setPageHeights] = useState([]);
  const [fieldSettingsModalProps, setFieldSettingsModalProps] = useState();
  const [saveForbiddenText, setSaveForbiddenText] = useState();
  const [assignSignersOrderModalVisible, setAssignSignersOrderModalVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateConfirm, setIsTemplateConfirm] = useState(false);

  const [templatePageRequest, setTemplatePageRequest] = useState({
    pageNumber: 1,
    pageSize: 5,
    sortBy: null,
    sortDirection: null,
    insuredId: window['ncFileManager']['insuredId'],
    searchString: null,
  });

  const dispatch = useDispatch();

  const { rabbitSignTemplatesLookup, rabbitSignTemplatesLookupLoading, saveRabbitSignTemplateLoading, deleteRabbitSignTemplateLoading } = useSelector(
    (state) => state.rabbitSign,
  );

  useEffect(() => {
    if (!isArray(emails) || isEmpty(emails)) {
      Modal.error({
        getContainer: rootContainer,
        title: 'The email to be sent is empty.',
      });
    } else {
      const requiredSigners = emails?.filter((element) => !element.nonRequiredToSign) || [];
      if (requiredSigners.length === 0) {
        Modal.info({
          getContainer: rootContainer,
          title: 'The selected email list includes unnecessary elements.',
        });
      }
    }

    if (isArray(rabbitSignFieldSettings) && !isEmpty(rabbitSignFieldSettings)) {
      setFields(rabbitSignFieldSettings);
    }

    api.getPdfFiles({ selectedFiles, combineAllPdfsIntoOne }).then((value) => {
      if (!isArray(value) || isEmpty(value)) {
        Modal.error({
          getContainer: rootContainer,
          title: 'The PDF file contains no content.',
        });
      }

      const resultFiles = value.map((x) => stringToArrayBuffer(x));

      setFileDataList(resultFiles);
      setCurrentFile(resultFiles[0]);
      setCurrentFileIndex(0);
      setHasMoreFiles(resultFiles.length > 1);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    let newSaveForbiddenText = '';
    if (!isArray(emails) || isEmpty(emails)) {
      newSaveForbiddenText += 'The email to be sent is empty.';
    }

    if (!isArray(fields) || isEmpty(fields)) {
      newSaveForbiddenText += 'Please configure a field for every email to be sent.';
    } else if (isArray(emails) && !isEmpty(emails)) {
      const completedEmails = fields.map((x) => x.email);
      const noRequiredEmails = emails.filter((element) => element.nonRequiredToSign).map((x) => x.email);

      const completedArray = uniq([...completedEmails, ...noRequiredEmails]);

      let isConfiguredForEveryEmail = isEmpty(emails?.filter((x) => !completedArray.includes(x.email)) ?? []);

      if (!isConfiguredForEveryEmail) {
        newSaveForbiddenText += 'Please configure a field for every email to be sent.';
      }
    }

    if (hasMoreFiles) {
      newSaveForbiddenText += ' Please go to the latest file.';
    }

    setSaveForbiddenText(newSaveForbiddenText);
  }, [fields, emails, hasMoreFiles]);

  const handleOkClick = () => {
    setRabbitSignFieldSettings(fields);

    onCancel();
  };
  //#region Template

  const hadleDeleteTemplateClick = (template) => {
    if (isGuid(template?.id)) {
      if (selectedTemplate?.id && selectedTemplate?.id == template?.id) {
        onSelectedTemplateChange(null);
      }

      dispatch(rabbitActions.deleteRabbitSignTemplate(template.id)).then((res) => {
        if (res) {
          const tempParams = { ...templatePageRequest, ...{ pageNumber: 1, pageSize: 5, searchString: null } };
          loadTemplates(tempParams);
        }
      });
    }
  };

  const handleUpdateTemplate = () => {
    Modal.confirm({
      getContainer: rootContainer,
      title: 'Update template',
      icon: <ExclamationCircleOutlined />,
      content: <div>Are you sure to update existing template?</div>,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        handleSaveTemplateClick(false, true);
      },
    });
  };

  const handleSaveTemplateClick = (isNew = false, skipNameCheck = false, templateName = '') => {
    let newTemplateName = '';
    if (isNew && !skipNameCheck) {
      Modal.confirm({
        getContainer: rootContainer,
        width: 450,
        title: 'New template name',
        content: (
          <Input
            placeholder="Please enter template name or leave empty"
            defaultValue={templateName}
            onChange={(e) => {
              newTemplateName = e.target.value;
            }}
          />
        ),
        okText: 'OK',
        onOk: () => {
          handleSaveTemplateClick(isNew, true, newTemplateName);
        },
        cancelButtonProps: { style: { display: 'none' } },
      });

      return;
    } else {
      newTemplateName = isEmpty(templateName) ? selectedTemplate?.name : templateName;
    }

    const rabbitFieldsArray = [];

    (selectedTemplate?.fields || fields).forEach((field) => {
      const found = emails?.find((element) => element.email == field.email);

      const newSettingfield = found ? { ...field, ...found } : (field = { ...field });
      rabbitFieldsArray.push(newSettingfield);
    });

    if (rabbitFieldsArray?.length) {
      const newTemplate = {
        id: isNew ? undefined : selectedTemplate?.id,
        insuredId: window['ncFileManager']['insuredId'],
        name: newTemplateName,
        rabbitSignFieldSettings: rabbitFieldsArray,
      };

      dispatch(rabbitActions.saveRabbitSignTemplate(newTemplate)).then((id) => {
        if (isGuid(id)) {
          dispatch(rabbitActions.getRabbitSignTemplateById(id)).then((templateData) => {
            onSelectedTemplateChange(templateData);
          });
        }
      });
    }
  };

  const onSelectedTemplateChange = (template) => {
    if (template?.rabbitSignFieldSettings?.length) {
      setSelectedTemplate(template);

      const selectedEmails = emails.map((obj) => obj.email);
      const templateEmails = uniq(template.rabbitSignFieldSettings.map((obj) => obj.email));

      const allEmailsPresentInTemplate = selectedEmails.every((r) => templateEmails.includes(r));

      if (allEmailsPresentInTemplate && selectedEmails.length == templateEmails.length) {
        //automatically apply settings by emails from templates
        const settings = template?.rabbitSignFieldSettings?.filter((element) => selectedEmails.includes(element.email));

        let drawIndex = 0;
        const newFields = [];
        if (settings?.length) {
          settings.forEach((element, elIndex) => {
            const newfield = mapRabbitSignField(element, ++drawIndex);

            newFields.push(newfield);
          });
        }
        setFields(newFields);
      } else {
        // show modal with template setting choosing
        setAssignSignersOrderModalVisible(true);
        setIsTemplateConfirm(true);
      }
    } else {
      setSelectedTemplate(null);
      setFields([]);
    }
  };

  const loadTemplates = (pageParams) => {
    setTemplatePageRequest(pageParams);

    dispatch(rabbitActions.getRabbitSignTemplates(pageParams));
  };

  //#endregion Template

  const handleNextFileClick = () => {
    const nextIndex = currentFileIndex + 1;

    setCurrentFileIndex(nextIndex);
    setCurrentFile(fileDataList[nextIndex]);
    setCurrentPageNumber(1);
    setHasMoreFiles(fileDataList.length > nextIndex + 1);
    setHasPrevFiles(nextIndex - 1 >= 0);
  };

  const handlePrevFileClick = () => {
    const nextIndex = currentFileIndex - 1;

    setCurrentFileIndex(nextIndex);
    setCurrentFile(fileDataList[nextIndex]);
    setCurrentPageNumber(1);
    setHasMoreFiles(fileDataList.length > nextIndex + 1);
    setHasPrevFiles(nextIndex - 1 >= 0);
  };

  const openNewFieldSettings = (event) => {
    const selectedFieldEmails = fields.map((f) => f.email);

    const unselectedClient = emails.find((c) => !selectedFieldEmails.includes(c.email));

    const defaultEmail = unselectedClient?.email;

    setFieldSettingsModalProps({ open: true, isNew: true, currentField: { x: event.x, y: event.y, email: defaultEmail } });
  };

  const openEditFieldSettings = (event) => {
    let item = fields.filter((x) => x.drawId === event.drawId)[0];

    setFieldSettingsModalProps({ open: true, isNew: false, currentField: item });
  };

  const handleEditClose = () => {
    setFieldSettingsModalProps({ open: false });
  };

  const actionsMenuItems = [
    {
      label: 'Configure signing order',
      key: 'rabbitSign-menu-action-item-1',
      onClick: () => setAssignSignersOrderModalVisible(true),
    },
    enabledTemplates
      ? {
          label: 'Choose template',
          key: 'rabbitSign-menu-action-item-2',
          onClick: () => {
            setTemplateModalVisible(true);
            const tempParams = { ...templatePageRequest, ...{ pageNumber: 1, pageSize: 5, searchString: null } };
            loadTemplates(tempParams);
          },
        }
      : null,
  ];

  return (
    <Modal
      width={1000}
      destroyOnClose={true}
      title="RabbitSign Fields Configuration"
      open={open}
      onOk={handleOkClick}
      onCancel={onCancel}
      okText="Save"
      cancelText="Close"
      centered
      okButtonProps={{ size: 'middle' }}
      cancelButtonProps={{ size: 'middle' }}
      getContainer={rootContainer}
      className="rabbitSign-field-configuration-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'left' }} id="rabbitSign-field-configuration-left-fotter">
            {enabledTemplates && (
              <Dropdown.Button
                icon={<DownOutlined />}
                loading={saveRabbitSignTemplateLoading}
                disabled={(isString(saveForbiddenText) && !isEmpty(saveForbiddenText)) || saveRabbitSignTemplateLoading}
                title={saveForbiddenText || (selectedTemplate?.name ? 'Chosen template name: ' + selectedTemplate?.name : '')}
                type="primary"
                size="small"
                getPopupContainer={() => document.getElementById('rabbitSign-field-configuration-left-fotter')}
                menu={{
                  items: [
                    {
                      label: 'Save as new template',
                      key: 'save-new-template',
                      onClick: () => handleSaveTemplateClick(true, false, selectedTemplate?.name),
                      disabled: !selectedTemplate?.id,
                    },
                  ],
                }}
                onClick={() => (!selectedTemplate?.id ? handleSaveTemplateClick(true, false) : handleUpdateTemplate())}
              >
                {!selectedTemplate?.id ? 'Save as template' : 'Save template'}
              </Dropdown.Button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'right' }}>
            <Button key="close" type="primary" onClick={onCancel}>
              Close
            </Button>

            {hasPrevFiles ? (
              <Button key="prevFile" type="primary" onClick={handlePrevFileClick}>
                Prev File
              </Button>
            ) : null}

            {hasMoreFiles ? (
              <Button key="nextFile" type="primary" onClick={handleNextFileClick}>
                Next File
              </Button>
            ) : null}

            <Button
              key="submit"
              type="primary"
              onClick={handleOkClick}
              disabled={isString(saveForbiddenText) && !isEmpty(saveForbiddenText)}
              title={saveForbiddenText}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '-14px 0px 10px 0px' }}>
        <div>Right click the PDF document to start placing the field</div>

        <div id="rabbitSign-field-configuration-actions">
          {!loading ? (
            <Dropdown
              menu={{ items: actionsMenuItems }}
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={() => document.getElementById('rabbitSign-field-configuration-actions')}
            >
              <Button type="primary" ghost size="small" style={{ fontSize: '12px' }}>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          ) : null}
        </div>
      </div>

      {!loading && emails?.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column' }}>
          {emails.map((emailInfo) => (
            <div key={'info-' + emailInfo.email} style={{ marginRight: '20px' }}>
              {getRabbitSignClientOptionNameWithClientPreffix(emailInfo)}
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <Spin spinning={true}>
          <div className="spinning-div"></div>
        </Spin>
      ) : (
        <PdfViewer
          data={currentFile}
          rootContainer={rootContainer}
          loading={loading}
          fields={fields}
          setFields={setFields}
          currentPageNumber={currentPageNumber}
          setCurrentPageNumber={setCurrentPageNumber}
          currentFileIndex={currentFileIndex}
          setPageHeights={setPageHeights}
          openNewFieldSettings={openNewFieldSettings}
          openEditFieldSettings={openEditFieldSettings}
          emails={emails}
        />
      )}

      {fieldSettingsModalProps && fieldSettingsModalProps.open === true ? (
        <EditSettingsModal
          rootContainer={rootContainer}
          open={fieldSettingsModalProps.open}
          fields={fields}
          setFields={setFields}
          pageHeights={pageHeights}
          currentFileIndex={currentFileIndex}
          currentPageNumber={currentPageNumber}
          isNew={fieldSettingsModalProps.isNew}
          currentField={fieldSettingsModalProps.currentField}
          handleClose={handleEditClose}
          emails={emails}
        />
      ) : null}
      {assignSignersOrderModalVisible ? (
        <AssignSignersOrderModal
          visible={assignSignersOrderModalVisible}
          signers={emails}
          fields={fields}
          setFields={setFields}
          template={selectedTemplate}
          setSignersOrder={(signers) => setSignersOrder(signers)}
          close={() => setAssignSignersOrderModalVisible(false)}
          isTemplateConfig={isTemplateConfirm}
          setIsTemplateConfirm={setIsTemplateConfirm}
          rootContainer={rootContainer}
        />
      ) : null}
      {templateModalVisible ? (
        <RabbitSignTemplateModal
          visible={templateModalVisible}
          close={(template) => {
            setTemplateModalVisible(false);
            onSelectedTemplateChange(template);
          }}
          cancel={() => setTemplateModalVisible(false)}
          rootContainer={rootContainer}
          hadleDeleteTemplateClick={hadleDeleteTemplateClick}
          loading={rabbitSignTemplatesLookupLoading || deleteRabbitSignTemplateLoading}
          pageRequest={templatePageRequest}
          loadData={loadTemplates}
          dataList={rabbitSignTemplatesLookup?.data}
          totalRecords={rabbitSignTemplatesLookup?.totalCount}
          totalSigners={emails?.length}
        />
      ) : null}
    </Modal>
  );
};

export default FieldSettingsModal;
